import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import ExpenseRow from "../components/ExpenseRow";
import { api } from "../lib/api";
import { useBackButton } from "../lib/telegram";
import { ExpenseOut } from "../lib/schemas";
import { Skeleton } from "../ui";

export default function History() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);

  const { data: expenses, isLoading } = useQuery({
    queryKey: ["history", groupId],
    queryFn: () => api<ExpenseOut[]>("GET", `/chats/${groupId}/history?limit=50`),
  });

  const handleBack = useCallback(() => nav(`/groups/${groupId}`), [nav, groupId]);
  useBackButton(handleBack);

  if (isLoading || !expenses) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
        <Skeleton className="h-4 w-24 mx-auto mb-6" />
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="border-t border-hairline first:border-t-0 px-4 py-3.5 flex items-center justify-between">
              <div className="flex-1 mr-4">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
      <h1 className="font-sans text-base font-semibold text-fg mb-6 text-center">
        History
      </h1>

      {expenses.length === 0 ? (
        <p className="text-muted text-center py-8">No expenses yet.</p>
      ) : (
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {expenses.map((expense, i) => (
            <motion.div
              key={expense.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.04 }}
            >
              <ExpenseRow
                expense={expense}
                currency={expense.currency}
                onClick={() =>
                  nav(`/groups/${groupId}/expenses/${expense.id}`, {
                    state: { expense },
                  })
                }
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
